<template>
  <b-modal
    modal-class="component-modal-product"
    id="createOrUpdatePurchaseRequestModal"
    ref="createOrUpdatePurchaseReuqestModel"
    size="xl"
    title="รายการใบขอซื้อ"
    no-close-on-backdrop
    @close="close"
    @show="listenForPaste"
    @hide="stopListening"
    @hidden="onHidden"
  >
    <div
      v-if="isFromMRP"
      class="pr-info-row"
    >
      <span class="pr-info-label">รหัสใบวางแผนความต้องการวัสดุ:</span>
      <span class="pr-info-value">{{ mrpNo }}</span>
    </div>

    <b-alert
      :show="!!clipboardOffer"
      variant="info"
      dismissible
      @dismissed="dismissClipboardOffer"
    >
      <div
        v-if="clipboardOffer"
        class="d-flex align-items-center justify-content-between"
      >
        <span>
          พบข้อมูลสินค้าในคลิปบอร์ด {{ clipboardOffer.validCount }} รายการ
        </span>
        <b-button
          size="sm"
          variant="primary"
          @click="useClipboardOffer"
          >ใช้ข้อมูลนี้</b-button
        >
      </div>
    </b-alert>

    <b-alert
      :show="!!pasteResult"
      :variant="pasteResult && pasteResult.skipped.length ? 'warning' : 'success'"
      dismissible
      @dismissed="pasteResult = null"
    >
      <template v-if="pasteResult">
        วางจาก Excel สำเร็จ {{ pasteResult.added + pasteResult.updated }} รายการ
        <span v-if="pasteResult.updated">
          (อัปเดตจำนวนสินค้าที่มีอยู่แล้ว {{ pasteResult.updated }} รายการ)
        </span>
        <span v-if="pasteResult.existing">
          (มีในตารางแล้ว {{ pasteResult.existing }} รายการ ไม่เปลี่ยนแปลง)
        </span>
        <div v-if="pasteResult.needQty">
          กรุณากรอกจำนวนให้ {{ pasteResult.needQty }} รายการที่วางมาเฉพาะรหัส
        </div>
        <div v-if="pasteResult.skipped.length">
          ข้าม {{ pasteResult.skipped.length }} แถว:
          <ul class="mb-0 pl-4">
            <li
              v-for="s in pasteResult.skipped.slice(0, 5)"
              :key="s.rowNo"
            >
              แถวที่ {{ s.rowNo }}<template v-if="s.text"> ({{ s.text }})</template>
              — {{ s.reason }}
            </li>
          </ul>
          <div v-if="pasteResult.skipped.length > 5">
            และอีก {{ pasteResult.skipped.length - 5 }} แถว
          </div>
        </div>
      </template>
    </b-alert>

    <b-table
      id="createPurchaseRequestTable"
      :items="items"
      :fields="fields"
      responsive
      hover
      small
      sort-icon-left
      head-variant="light"
    >
      <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>

      <template v-slot:cell(image)="row">
        <ProductImage
          :src="(row.item.product && row.item.product.imageUrl) || null"
          :disabled="!row.item.product"
        />
      </template>

      <template v-slot:cell(product)="row">
        <MultipleSelectField
          :value="row.item.product"
          :options="getProductOptionsForRow(row.index)"
          :is-required="true"
          :is-error="row.item.productTouched && !row.item.product"
          placeholder="เลือกสินค้า"
          track-by="productId"
          :custom-label="productLabel"
          :show-error-icon="false"
          @input="onProductChange(row.index, $event)"
        />
      </template>

      <template v-slot:cell(qty)="row">
        <InputNumberField
          :adjust-stock="row.item.qty"
          :min="0"
          :show-buttons="true"
          :is-required="true"
          :allow-decimal="false"
          :invalid="row.item.qtyTouched && !row.item.qty"
          :disabled="!row.item.product"
          :on-change="val => onQtyChange(row.index, val)"
          :show-error-icon="false"
        />
      </template>

      <template v-slot:cell(unit)="row">
        <MultipleSelectField
          :isRequired="true"
          v-model="row.item.unit"
          :options="getUnitOptions(row.item)"
          :isError="
            !!row.item.product && !row.item.unit && row.item.productTouched
          "
          placeholder="เลือกหน่วยสินค้า"
          trackBy="id"
          :customLabel="unitLabel"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          :disabled="!row.item.product"
          :allowEmpty="false"
          :show-error-icon="false"
        />
      </template>

      <template v-slot:cell(actions)="row">
        <button
          class="delete-btn"
          :disabled="items.length <= 1"
          @click="removeItem(row.index)"
          v-b-tooltip.hover.top="'ลบหน่วยสินค้า'"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </template>

      <template v-slot:bottom-row>
        <td
          colspan="6"
          class="bottom-row-cell"
        >
          <b-button
            type="button"
            variant="outline-primary"
            @click="addItem"
            class="bottom-add-row"
          >
            <i class="fas fa-plus"></i> เพิ่มรายการสินค้า
          </b-button>
          <span class="paste-hint">
            หรือคัดลอกจาก Excel แล้ววาง (Ctrl+V) — คอลัมน์: รหัสสินค้า | จำนวน |
            หน่วย (คัดลอกเฉพาะรหัสก็ได้ แล้วกรอกจำนวนเอง / ไม่ระบุหน่วย =
            หน่วยหลัก)
          </span>
        </td>
      </template>
    </b-table>

    <template v-slot:modal-footer>
      <span
        v-if="isCreate()"
        class="footer-warning-text"
      >
        การสร้างรายการจะเป็นการส่งข้อมูลให้ผู้จัดการอนุมัติ
        กรุณาตรวจสอบข้อมูลให้ถูกต้อง
      </span>
      <b-button
        v-if="isCreate()"
        variant="primary"
        @click="create"
        >สร้างรายการ</b-button
      >
      <b-button
        v-else
        variant="primary"
        @click="update"
        >แก้ไขรายการ</b-button
      >
    </template>

    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Loading from "@/components/Loading";
import "@/theme/modal/ProductAndStock/Product.scss";
import MultipleSelectField from "@/components/table/stock/fields/MultipleSelectField.vue";
import InputNumberField from "@/components/table/stock/fields/InputNumberField.vue";
import { mapActions } from "vuex";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import { Status } from "@/constants/productStock";
import {
  isTablePaste,
  parsePrPaste,
  mergePastedLines,
} from "@/utils/prPasteParser";

const createEmptyItem = () => ({
  product: null,
  qty: 0,
  unit: null,
  productTouched: false,
  qtyTouched: false,
});

export default {
  name: "CreateOrUpdatePurchaseReuqestModel",
  components: {
    Loading,
    MultipleSelectField,
    InputNumberField,
    ProductImage,
  },
  data() {
    return {
      isLoading: false,
      submitted: false,
      type: "CREATE",
      prId: null,
      mrpNo: null,
      mrpId: null,
      productList: [],
      pasteResult: null,
      clipboardOffer: null,
      handledClipboardText: null,
      items: [createEmptyItem()],
      fields: [
        { key: "index", label: "", thStyle: { width: "4%" } },
        { key: "image", label: "", thStyle: { width: "5%" } },
        {
          key: "product",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "41%" },
        },
        { key: "qty", label: "จำนวน", thStyle: { width: "15%" } },
        { key: "unit", label: "หน่วยสินค้า", thStyle: { width: "15%" } },
        {
          key: "actions",
          label: "",
          thStyle: { width: "6%", minWidth: "60px" },
          class: "text-center",
        },
      ],
    };
  },
  beforeDestroy() {
    this.stopListening();
  },
  computed: {
    selectedProductIds() {
      return this.items.map(i => i.product?.productId).filter(id => id != null);
    },
    isFromMRP() {
      return !!this.mrpId;
    },
  },
  methods: {
    ...mapActions({
      createPurchaseReuqest: "modulePurchaseRequest/createPurchaseReuqest",
      updatePurchaseRequest: "modulePurchaseRequest/updatePurchaseRequest",
      fetchMrpList: "moduleMrp/fetchMrpList",
      fetchPurchaseRequests: "modulePurchaseRequest/fetchPurchaseRequests",
    }),
    show({ prId, mrpNo, mrpId, products, productList, type }) {
      this.prId = prId || null;
      this.mrpNo = mrpNo || null;
      this.mrpId = mrpId || null;
      this.type = type;
      this.productList = productList || [];
      this.items = products?.length
        ? products.map(p => {
            const matched =
              this.productList.find(
                pl =>
                  pl.productId === p.productId ||
                  pl.productId === p.id ||
                  pl.productCode === p.no ||
                  pl.productCode === p.productCode
              ) || null;
            const defaultUnit = matched?.productUnit
              ? {
                  id: matched.productUnit.id,
                  name: matched.productUnit.description,
                }
              : p.uom
              ? { id: p.uom.id, name: p.uom.description }
              : p.uomId
              ? { id: p.uomId, name: p.uomName || "" }
              : null;
            return {
              product: matched,
              qty: p.quantity ?? 1,
              unit: defaultUnit,
              productTouched: false,
              qtyTouched: false,
            };
          })
        : [createEmptyItem()];
      this.$bvModal.show("createOrUpdatePurchaseRequestModal");
    },
    listenForPaste() {
      document.addEventListener("paste", this.onPaste);
      window.addEventListener("focus", this.checkClipboard);
      this.checkClipboard();
    },
    stopListening() {
      document.removeEventListener("paste", this.onPaste);
      window.removeEventListener("focus", this.checkClipboard);
    },
    onHidden() {
      this.stopListening();
      this.resetForm();
    },
    onPaste(event) {
      const text = event.clipboardData?.getData("text/plain");
      if (!isTablePaste(text)) return;
      event.preventDefault();
      this.applyPastedText(text);
    },
    applyPastedText(text) {
      this.handledClipboardText = text;
      this.clipboardOffer = null;

      const { lines, skipped } = parsePrPaste(text, this.productList);
      if (lines.length === 0) {
        this.pasteResult = {
          added: 0,
          updated: 0,
          existing: 0,
          needQty: 0,
          skipped,
        };
        return;
      }
      const { items, ...counts } = mergePastedLines(this.items, lines);
      this.items = items;
      this.pasteResult = { ...counts, skipped };
    },
    // Offer to use whatever spreadsheet data is already on the clipboard.
    // Browsers may refuse (no permission / not focused / unsupported); then we
    // just stay quiet and Ctrl+V keeps working.
    async checkClipboard() {
      try {
        if (!navigator.clipboard?.readText) return;
        const text = await navigator.clipboard.readText();
        if (!isTablePaste(text) || text === this.handledClipboardText) return;

        const { lines } = parsePrPaste(text, this.productList);
        if (lines.length === 0) return;
        this.clipboardOffer = { text, validCount: lines.length };
      } catch (err) {
        // permission denied / document not focused: nothing to offer
      }
    },
    useClipboardOffer() {
      if (this.clipboardOffer) this.applyPastedText(this.clipboardOffer.text);
    },
    // b-alert also emits "dismissed" when we hide it ourselves (offer already
    // cleared), so only remember the text while an offer is actually showing.
    dismissClipboardOffer() {
      if (this.clipboardOffer) {
        this.handledClipboardText = this.clipboardOffer.text;
      }
      this.clipboardOffer = null;
    },
    isCreate() {
      return this.type === "CREATE";
    },
    isUpdate() {
      return this.type === "UPDATE";
    },
    close() {
      this.$bvModal.hide("createOrUpdatePurchaseRequestModal");
    },
    resetForm() {
      this.submitted = false;
      this.prId = null;
      this.mrpNo = null;
      this.mrpId = null;
      this.productList = [];
      this.pasteResult = null;
      this.clipboardOffer = null;
      this.handledClipboardText = null;
      this.items = [createEmptyItem()];
    },
    productLabel(item) {
      if (!item) return "";
      return `${item.productCode} - ${item.productNameMain}`;
    },
    getProductOptionsForRow(index) {
      const currentId = this.items[index]?.product?.productId;
      return this.productList.filter(
        p =>
          (p.productStatus !== Status.ARCHIVED || p.productId === currentId) &&
          (!this.selectedProductIds.includes(p.productId) ||
            p.productId === currentId)
      );
    },
    getUnitOptions(item) {
      if (!item.product) return [];
      const units = [];
      if (item.product.productUnit) {
        units.push({
          id: item.product.productUnit.id,
          name: item.product.productUnit.description,
        });
      }
      const conversions = item.product.productConversionUnitItems || [];
      for (const c of conversions) {
        const uom = c.uom || c.productSubUnit || null;
        if (uom && uom.id && !units.find(u => u.id === uom.id)) {
          units.push({ id: uom.id, name: uom.description });
        }
      }
      return units;
    },
    unitLabel(item) {
      return item?.name || item?.description || "";
    },
    onProductChange(index, product) {
      const item = { ...this.items[index], product };
      if (product?.productUnit) {
        item.unit = {
          id: product.productUnit.id,
          name: product.productUnit.description,
        };
      } else {
        item.unit = null;
      }
      item.qtyTouched = false;
      this.$set(this.items, index, item);
    },
    onUnitChange(index, unit) {
      this.$set(this.items, index, { ...this.items[index], unit });
    },
    onQtyChange(index, val) {
      const qty = val == null ? null : parseInt(val, 10);
      this.$set(this.items, index, { ...this.items[index], qty });
    },
    addItem() {
      this.items.push(createEmptyItem());
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    async create() {
      this.items.forEach((item, i) =>
        this.$set(this.items, i, {
          ...item,
          productTouched: true,
          qtyTouched: true,
        })
      );
      const isValid = this.items.every(i => i.product && i.qty && i.unit);
      if (!isValid) return;
      this.isLoading = true;
      try {
        const payload = {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          lines: this.items.map(i => ({
            id: i.product.productId,
            quantity: i.qty,
            uomId: i.unit.id,
          })),
        };

        // Only include mrpId when creating from MRP
        if (this.isFromMRP) {
          payload.mrpId = this.mrpId;
        }

        await this.createPurchaseReuqest(payload);

        const successMsg = this.isFromMRP
          ? "แปลงรายการใบวางแผนความต้องการวัสดุเป็นใบขอซื้อสำเร็จ"
          : "เพิ่มรายการใบขอซื้อสำเร็จ";
        this.$parent.showDialogToast("success", successMsg);

        if (this.isFromMRP) {
          this.fetchMrpList({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          });
        }
        this.fetchPurchaseRequests({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        });
        this.$emit("success");
        this.close();
      } catch (err) {
        console.error("createPurchaseReuqest failed", err);
        const errorMsg = this.isFromMRP
          ? "แปลงรายการใบวางแผนความต้องการวัสดุเป็นใบขอซื้อไม่สำเร็จ กรุณาลองใหม่"
          : "สร้างใบขอซื้อไม่สำเร็จ กรุณาลองใหม่";
        this.$parent.showDialogToast("danger", errorMsg);
      } finally {
        this.isLoading = false;
      }
    },
    async update() {
      this.items.forEach((item, i) =>
        this.$set(this.items, i, {
          ...item,
          productTouched: true,
          qtyTouched: true,
        })
      );
      const isValid = this.items.every(i => i.product && i.qty && i.unit);
      if (!isValid) return;
      this.isLoading = true;
      try {
        await this.updatePurchaseRequest({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          prId: this.prId,
          lines: this.items.map(i => ({
            id: i.product.productId,
            quantity: i.qty,
            uomId: i.unit.id,
          })),
        });
        this.$parent.showDialogToast("success", "แก้ไขรายการใบขอซื้อสำเร็จ");
        this.fetchPurchaseRequests({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        });
        this.$emit("success");
        this.close();
      } catch (err) {
        console.error("updatePurchaseRequest failed", err);
        this.$parent.showDialogToast(
          "danger",
          "แก้ไขรายการใบขอซื้อไม่สำเร็จ กรุณาลองใหม่"
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.footer-warning-text {
  font-size: 14px;
  font-weight: 400;
  color: #6d747d;
  margin-left: auto;
  margin-right: 20px;
}

.pr-info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 10px;
}

.pr-info-label {
  font-weight: 600;
  white-space: nowrap;
  color: #212529;
}

.pr-info-value {
  color: #212529;
}

.paste-hint {
  margin-left: 12px;
  font-size: 13px;
  color: #6d747d;
}

.add-item-btn {
  padding: 0;
  font-size: 0.9rem;
  text-decoration: none;
}

.add-item-btn:hover {
  text-decoration: underline;
}

::v-deep .modal-body {
  padding: 16px 16px 0 !important;
}

::v-deep .component-modal-product .modal-dialog {
  max-width: 1300px;
}

::v-deep .table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;
}

::v-deep .b-table {
  min-width: 750px;
}

::v-deep td {
  padding: 8px;
  vertical-align: middle !important;
}

::v-deep th {
  vertical-align: middle !important;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  margin-top: 5px;
}

::v-deep #createOrUpdatePurchaseRequestTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep #createOrUpdatePurchaseRequestTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep
  #createOrUpdatePurchaseRequestTable.table-hover
  tbody
  tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep .delete-btn {
  background-color: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: #a3a4a6;
  font-size: 20px;
  width: 48px;
  height: 38px;
}

.delete-btn:hover:not(:disabled) {
  color: #d33;
}

.delete-btn:disabled {
  color: #ebebeb !important;
  cursor: not-allowed;
}
</style>
